import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauth, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network/server error during logout is non-fatal — we still clear local
      // auth state so the user is signed out client-side regardless.
      console.warn("Logout request failed (ignored):", err?.message || err);
    }
    setUser(false);
  }, []);

  const changePassword = useCallback(async (old_password, new_password) => {
    await api.post("/auth/change-password", { old_password, new_password });
  }, []);

  return (
    <AuthContext.Provider value={useMemo(
      () => ({ user, setUser, login, logout, refresh, changePassword }),
      [user, login, logout, refresh, changePassword],
    )}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiErrorDetail };
